import React, { createRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';

import Slider from 'shared-components/Slider/Slider';
import SliderControls from 'shared-components/SliderControls/SliderControls';

import styles from './Carousel.module.css';

const Carousel = ({
  children,
  setParentSliderWidth,
  slideCount,
  receiveCurrentSlide,
  carouselHeightClassName,
  handleClickControls,
  cardMargin,
}) => {
  const [currSlide, setCurrSlide] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [swipeDelta, setSwipeDelta] = useState(0);

  const sliderFrame = createRef();

  // this returns the starting position of the carousel content container
  // we need to subtract the current slide * card margin
  // the product cards have an additional space between the last card of the current slide and first card of next slide
  const getLeftPosition = () => {
    return -sliderWidth * currSlide + swipeDelta - currSlide * cardMargin;
  };

  const handleSwipeStart = (e) => {
    const touchEvent = e.targetTouches[0];
    const swipeStartX = touchEvent.pageX;
    setSwipeStartX(swipeStartX);
    setSwiping(true);
  };

  const handleSwipeMove = _.throttle(
    (e) => {
      const x = e.targetTouches[0].pageX;
      let newSwipeDelta = x - swipeStartX;
      const swipeIsInvalid =
        (currSlide === 0 && newSwipeDelta > 0) ||
        (currSlide === slideCount - 1 && newSwipeDelta < 0);
      newSwipeDelta = swipeIsInvalid ? 0 : newSwipeDelta;
      setSwipeDelta(newSwipeDelta);
    },
    100,
    { trailing: false }
  );

  const handleSwipeEnd = () => {
    const swipingLeft = swipeDelta < 0;
    const shouldUpdateCurrSlide = Math.abs(swipeDelta) > 5;
    setSwipeDelta(0);
    setSwipeStartX(0);
    setSwiping(false);
    if (shouldUpdateCurrSlide) {
      setCurrSlide((prevState) =>
        swipingLeft ? prevState + 1 : prevState - 1
      );
    }
  };

  const toggleCurrSlide = (delta) => {
    setCurrSlide((prevState) => prevState + delta);
  };

  const handleSliderWidth = () => {
    const newSliderWidth =
      sliderFrame.current && sliderFrame.current.clientWidth;
    // we want to update the slider width value for both the parent and the current container
    // the parent uses this value to calculate the width of the children
    if (newSliderWidth) {
      setSliderWidth(newSliderWidth);
      setParentSliderWidth(newSliderWidth);
    }
  };

  const handleClickLeft = () => {
    // handleClickControls is used for tracking this click
    if (handleClickControls) handleClickControls();
    toggleCurrSlide(-1);
  };

  const handleClickRight = () => {
    // handleClickControls is used for tracking this click
    if (handleClickControls) handleClickControls();
    toggleCurrSlide(1);
  };

  // this handles resizing the carousel on loading the page, the slider frame ref updating, and resizing the window
  useEffect(() => {
    handleSliderWidth();
    window.addEventListener('resize', handleSliderWidth);
    return () => {
      window.removeEventListener('resize', handleSliderWidth);
    };
  }, [sliderFrame]);

  // this updates the parent with the current slide so that it can display the current slide
  useEffect(() => {
    if (receiveCurrentSlide) {
      receiveCurrentSlide(currSlide);
    }
  }, [currSlide]);

  return (
    <div className={styles.container}>
      <div
        onTouchStart={handleSwipeStart}
        onTouchMove={handleSwipeMove}
        onTouchEnd={handleSwipeEnd}
        ref={sliderFrame}
        // we need to explicity set a height here because the slider component needs a container height
        className={classNames(styles.slider, carouselHeightClassName)}
      >
        <Slider
          currSlide={currSlide}
          left={getLeftPosition()}
          swiping={swiping}
          count={slideCount}
          sliderWrapperClassName={styles.sliderWrapper}
        >
          {children}
        </Slider>
      </div>
      <SliderControls
        currSlide={currSlide}
        count={slideCount}
        updateCurrSlide={(i) => setCurrSlide(i)}
        onClickLeft={handleClickLeft}
        onClickRight={handleClickRight}
        sliderButtonClassName={styles.sliderButton}
      />
    </div>
  );
};

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  setParentSliderWidth: PropTypes.func.isRequired,
  slideCount: PropTypes.number.isRequired,
  receiveCurrentSlide: PropTypes.func,
  carouselHeightClassName: PropTypes.string.isRequired,
  handleClickControls: PropTypes.func,
  cardMargin: PropTypes.number,
};

Carousel.defaultProps = {
  handleClickControls: null,
};

Carousel.defaultProps = {
  receiveCurrentSlide: () => {},
  cardMargin: 0,
};

export default Carousel;
